import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(
    Math.random().toString(36).slice(2) + Date.now().toString(36)
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streaming]);

  const send = async (text) => {
    if (!text.trim() || streaming) return;
    const history = messages.map((m) => ({ role: m.role, content: m.text })).slice(-6);
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/ai/assistant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: text,
          lang,
          history,
          session_id: sessionRef.current,
        }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const c = [...m];
              const last = c[c.length - 1];
              c[c.length - 1] = { ...last, text: last.text + payload.delta };
              return c;
            });
          }
          if (payload.error) throw new Error(payload.error);
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        const last = c[c.length - 1];
        if (last && last.role === "assistant" && !last.text) {
          c[c.length - 1] = { ...last, text: t.ai.error };
        }
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  const lastIsEmptyAssistant =
    streaming && messages[messages.length - 1]?.role === "assistant" &&
    !messages[messages.length - 1]?.text;

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: EASE }}
            data-testid="ai-chat-panel"
            className="clip-frame-sm fixed bottom-24 right-4 z-[65] flex h-[540px] max-h-[72vh] w-[390px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden border border-white/15 bg-deep shadow-2xl sm:right-6"
          >
            <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-navy/60 p-4">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center border border-white/10 bg-panel/60 text-steel">
                  <i className="fa-solid fa-microchip" />
                </span>
                <div>
                  <div className="font-display text-lg font-semibold uppercase leading-none text-white">
                    {t.ai.title}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400">{t.ai.tagline}</div>
                </div>
              </div>
              <button
                data-testid="ai-chat-close"
                onClick={() => setOpen(false)}
                aria-label="Close assistant"
                className="grid h-9 w-9 place-items-center rounded-full border border-white/15 text-slate-300 transition-colors hover:text-white"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
              {messages.length === 0 && (
                <>
                  <div className="mr-auto max-w-[90%] border border-white/10 bg-navy/70 px-4 py-3 text-sm leading-relaxed text-slate-200">
                    {t.ai.welcome}
                  </div>
                  <div className="flex flex-col gap-2 pt-1">
                    {t.ai.suggestions.map((s, i) => (
                      <button
                        key={i}
                        data-testid={`ai-chat-suggestion-${i}`}
                        onClick={() => send(s)}
                        className="w-fit border border-steel/30 px-3 py-2 text-left text-xs text-steel transition-colors hover:bg-steel/10"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {messages.map((m, i) =>
                m.role === "user" ? (
                  <div
                    key={i}
                    data-testid={`ai-chat-msg-user-${i}`}
                    className="ml-auto max-w-[85%] bg-crimson px-4 py-2.5 text-sm text-white"
                  >
                    {m.text}
                  </div>
                ) : (
                  <div
                    key={i}
                    data-testid={`ai-chat-msg-assistant-${i}`}
                    className="mr-auto max-w-[90%] whitespace-pre-wrap border border-white/10 bg-navy/70 px-4 py-3 text-sm leading-relaxed text-slate-200"
                  >
                    {m.text.replace(/\*\*/g, "").replace(/^\*\s/gm, "• ")}
                    {i === messages.length - 1 && lastIsEmptyAssistant && (
                      <span className="mt-1 flex gap-1">
                        <i className="fa-solid fa-circle text-[5px] text-steel" />
                        <i className="fa-solid fa-circle text-[5px] text-steel" />
                        <i className="fa-solid fa-circle text-[5px] text-steel" />
                      </span>
                    )}
                  </div>
                )
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send(input);
              }}
              className="flex items-center gap-2 border-t border-white/10 p-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="ai-chat-input"
                placeholder={t.ai.placeholder}
                className="min-w-0 flex-1 border border-white/10 bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors placeholder:text-slate-600 focus:border-steel/70"
              />
              <button
                type="submit"
                data-testid="ai-chat-send"
                disabled={streaming || !input.trim()}
                aria-label={t.ai.send}
                className="grid h-10 w-10 shrink-0 place-items-center bg-crimson text-white transition-colors hover:bg-crimson-hover disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className={`fa-solid ${streaming ? "fa-spinner animate-spin" : "fa-paper-plane"}`} />
              </button>
            </form>

            <div className="border-t border-white/5 px-4 py-2 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
              {t.ai.powered}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label="AI assistant"
        className="fixed bottom-6 right-4 z-[65] grid h-14 w-14 place-items-center rounded-full bg-crimson text-white shadow-xl transition-colors hover:bg-crimson-hover sm:right-6"
      >
        <i className={`fa-solid text-lg ${open ? "fa-xmark" : "fa-comments"}`} />
      </button>
    </>
  );
}
